import { useEffect, useState } from "react";
import { ShieldCheck, KeyRound, Plus, Trash2, Loader2, Copy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";
import { BillingPanel } from "../components/BillingPanel";

export const Settings = () => {
  const { user } = useAuth();
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyLabel, setNewKeyLabel] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  useEffect(() => {
    if (user) setPhone(user.phone || "");
  }, [user]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/auth/api-keys`)
      .then((res) => setApiKeys(res.data))
      .catch(() => {});
  }, [user]);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/auth/me`, { phone });
      toast.success("Account settings saved.");
    } catch {
      toast.error("Could not save settings.");
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!newKeyLabel.trim()) return;
    setCreatingKey(true);
    try {
      const { data } = await axios.post(`${API}/auth/api-keys`, { label: newKeyLabel.trim() });
      setRevealedKey(data.key);
      setApiKeys((prev) => [data, ...prev]);
      setNewKeyLabel("");
      toast.success("M2M API key generated - copy it now.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate key.");
    } finally {
      setCreatingKey(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    setRevokingId(keyId);
    try {
      await axios.delete(`${API}/auth/api-keys/${keyId}`);
      setApiKeys((prev) => prev.map((k) => (k.id === keyId ? { ...k, revoked_at: new Date().toISOString() } : k)));
      toast.success("API key revoked.");
    } catch {
      toast.error("Could not revoke key.");
    } finally {
      setRevokingId(null);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(revealedKey);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Could not copy. Copy manually.");
    }
  };

  if (!user) return null;

  return (
    <div className="mx-auto max-w-lg px-6 py-12" data-testid="settings-page">
      <h1 className="mb-6 flex items-center gap-2 font-heading text-2xl font-bold text-white">
        <ShieldCheck className="h-5 w-5 text-sovereign" /> Account Settings
      </h1>
      <div className="space-y-5 rounded-lg border border-surface-border bg-surface-card p-6">
        <div>
          <Label className="text-slate-400">Email</Label>
          <Input value={user.email} disabled className="mt-1" data-testid="settings-email-input" />
        </div>
        <div>
          <Label className="text-slate-400">Phone Number</Label>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            className="mt-1"
            data-testid="settings-phone-input"
          />
          <p className="mt-1 text-xs text-slate-500">
            Used for SMS delivery of Sovereign Defense Threat Briefings (once SMS is enabled on your account).
          </p>
        </div>
        {typeof user.credit_balance === "number" && (
          <div className="rounded-md border border-truth-green/30 bg-truth-green/5 p-3" data-testid="settings-credit-balance">
            <p className="text-xs uppercase tracking-wide text-slate-500">Account Credit</p>
            <p className="font-heading text-xl font-bold text-truth-green">${user.credit_balance.toFixed(2)}</p>
          </div>
        )}
        <Button onClick={save} disabled={loading} className="bg-sovereign text-white hover:bg-sovereign/90" data-testid="settings-save-btn">
          {loading ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      <BillingPanel />

      <div className="mt-8 rounded-lg border border-surface-border bg-surface-card p-6" data-testid="settings-api-keys-panel">
        <div className="mb-1 flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-openai-cyan" />
          <h2 className="font-heading text-lg font-bold text-white">Remote Machine (M2M) API Keys</h2>
        </div>
        <p className="mb-4 text-sm text-slate-400">
          Let one of your own machines authenticate directly to BrandSignal to push its own node Pulse Check, without your login session.
        </p>

        {revealedKey && (
          <div className="mb-4 rounded-md border border-warning-amber/50 bg-warning-amber/5 p-3" data-testid="settings-revealed-key">
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-warning-amber">Copy this key now - it will not be shown again</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate text-xs text-white">{revealedKey}</code>
              <Button size="sm" onClick={copyKey} className="gap-1 bg-warning-amber text-obsidian hover:bg-warning-amber/90" data-testid="settings-copy-key-btn">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        {apiKeys.length > 0 && (
          <div className="mb-4 space-y-2" data-testid="settings-api-keys-list">
            {apiKeys.map((k) => (
              <div key={k.id} className="flex items-center justify-between rounded-md border border-surface-border bg-obsidian p-3" data-testid={`settings-api-key-${k.id}`}>
                <div>
                  <p className="text-sm font-semibold text-white">{k.label}</p>
                  <p className="text-xs text-slate-500">{k.prefix}_&hellip; {k.revoked_at ? "(revoked)" : ""}</p>
                </div>
                {!k.revoked_at && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={revokingId === k.id}
                    onClick={() => revokeApiKey(k.id)}
                    className="gap-1 border-sovereign/40 text-sovereign hover:bg-sovereign/10"
                    data-testid={`settings-revoke-key-${k.id}`}
                  >
                    {revokingId === k.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Input
            value={newKeyLabel}
            onChange={(e) => setNewKeyLabel(e.target.value)}
            placeholder="Key label (e.g. Edge Node 1)"
            data-testid="settings-new-key-label-input"
          />
          <Button onClick={createApiKey} disabled={creatingKey || apiKeys.filter((k) => !k.revoked_at).length >= 5} className="gap-2 bg-openai-cyan text-obsidian hover:bg-openai-cyan/90" data-testid="settings-generate-key-btn">
            {creatingKey ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Generate Key
          </Button>
        </div>
      </div>
    </div>
  );
};
